import { memo, useState } from 'react';

import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';

import { PERIOD_OPTIONS, TOOLTIP_CONTENT } from './constant';
import { ContainerPeriod, StyledIconButton } from './style';
import { PeriodType, prePeriodGeneric } from './interface';
import { CustomPeriodMenu } from './custom-period-menu';

import { useSelectPeriod } from './hook';
import { fixMinMaxForDatePicker } from './fix-timezone';
import { FaRegCalendar } from '@react-icons/all-files/fa/FaRegCalendar';
import { Tooltip } from '../tooltip';

interface IProps {
  periodOptions?: typeof PERIOD_OPTIONS;
  prePeriod?: prePeriodGeneric | undefined;
  onChangePeriod: (period: string) => void;
  minStartDate?: number;
  maxEndDate?: number;
  loading?: boolean;
  showExplanation?: boolean;
  showCustom?: boolean;
  sx?: any;
}

export const PeriodSelect = memo<IProps>(function PeriodSelect({
  prePeriod,
  onChangePeriod,
  minStartDate,
  maxEndDate,
  loading,
  showExplanation = true,
  showCustom = true,
  periodOptions = PERIOD_OPTIONS,
  sx,
}) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLButtonElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const { period, onSelectPeriod, formatPeriodMessage, isCustomOrMonths } =
    useSelectPeriod({
      preSelectedPeriod: prePeriod,
      onChangePeriod,
      minStartDate,
      maxEndDate,
    });

  if (loading) {
    return <Skeleton variant="rectangular" width={200} height={40} />;
  }

  return (
    <Stack gap={1}>
      <ContainerPeriod sx={sx}>
        {periodOptions.map((option) => (
          <StyledIconButton
            key={option.value}
            title={option.label}
            onClick={() =>
              onSelectPeriod({
                type: option.label as PeriodType,
                value: option.value,
              })
            }
            sx={{
              fontWeight: 400,
            }}
            selected={
              period.type === option.label || period.type === option.value
            }
          >
            <Typography variant="body2">{option.label}</Typography>
          </StyledIconButton>
        ))}

        {showCustom && (
          <>
            <Divider orientation="vertical" variant="middle" flexItem />
            <Tooltip text="Período personalizado">
              <span>
                <StyledIconButton
                  title="Período personalizado"
                  selected={
                    period.type === 'CUSTOM' || period.type === 'MONTHS'
                  }
                  sx={{ py: '10px' }}
                  onClick={handleClick}
                >
                  <FaRegCalendar
                    size={16}
                    // color={selected ? '#0F132A' : undefined}
                  />
                </StyledIconButton>
              </span>
            </Tooltip>
            <CustomPeriodMenu
              period={period}
              onSelectPeriod={onSelectPeriod}
              minStartDate={fixMinMaxForDatePicker(minStartDate)}
              maxEndDate={fixMinMaxForDatePicker(maxEndDate)}
              triggerRef={anchorEl}
              handleClose={handleClose}
              open={open}
            />
          </>
        )}
      </ContainerPeriod>

      <Stack
        display={!showExplanation && !isCustomOrMonths ? 'none' : 'flex'}
        direction="row"
        alignItems="center"
        gap={1}
        justifyContent={isCustomOrMonths ? 'space-between' : 'flex-end'}
      >
        {isCustomOrMonths && (
          <Typography variant="caption">
            Período: {formatPeriodMessage()}
          </Typography>
        )}

        {showExplanation && (
          <Stack
            direction="row"
            gap={1}
            alignItems="center"
            sx={{
              bgcolor: '#4FA6E51F',
              borderRadius: '4px',
              px: 1,
              maxWidth: 'fit-content',
            }}
          >
            <Tooltip
              size={14}
              text={TOOLTIP_CONTENT}
              title="Entenda o cálculo"
            />
          </Stack>
        )}
      </Stack>
    </Stack>
  );
});

export * from './use-period';
